import { ExternalLink } from "lucide-react";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";

/** Same entry point as accountant invitation onboarding (CJPPU consultas). */
export const CJPPU_CONSULTAS_LOGIN_URL =
  "https://consultas.cjppu.org.uy/webcafi/loginC.php?noUser";

export type CjppuCertificateInstructionsVariant = "accountant" | "generic";

export interface CjppuCertificateInstructionStepsProps {
  variant?: CjppuCertificateInstructionsVariant;
}

/**
 * Numbered steps + link only (no section title). For use inside accordions or cards.
 */
export function CjppuCertificateInstructionSteps({
  variant = "generic",
}: CjppuCertificateInstructionStepsProps): JSX.Element {
  const { t } = useTranslation();
  const step3Key =
    variant === "accountant"
      ? "auth.acceptAccountantInvitation.certificateStep3"
      : "cjppu.certificateStep3";

  return (
    <>
      <ol className="list-decimal space-y-1.5 pl-5 text-sm leading-relaxed text-muted-foreground marker:text-muted-foreground/70">
        <li>{t("auth.acceptAccountantInvitation.certificateStep1")}</li>
        <li>{t("auth.acceptAccountantInvitation.certificateStep2")}</li>
        <li>{t(step3Key)}</li>
        <li>{t("auth.acceptAccountantInvitation.certificateStep4")}</li>
      </ol>
      <a
        href={CJPPU_CONSULTAS_LOGIN_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1.5 text-sm font-medium text-primary underline-offset-4 transition-colors hover:text-primary/90 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {t("auth.acceptAccountantInvitation.certificateOpenCjppu")}
        <ExternalLink className="h-3.5 w-3.5" aria-hidden />
      </a>
    </>
  );
}

export interface CjppuCertificateInstructionsProps {
  className?: string;
  /**
   * `accountant`: step 3 mentions certifying for Gestionalo (invitation onboarding).
   * `generic`: neutral step 3 for other flows (e.g. income certificate).
   */
  variant?: CjppuCertificateInstructionsVariant;
}

/**
 * Full muted card with title + CJPPU steps (accountant invitation step 3 UI).
 */
export function CjppuCertificateInstructions({
  className,
  variant = "generic",
}: CjppuCertificateInstructionsProps): JSX.Element {
  const { t } = useTranslation();

  return (
    <div
      className={cn(
        "space-y-3 rounded-xl border border-border bg-muted/30 p-5 sm:p-6 dark:bg-muted/20",
        className,
      )}
    >
      <h2 className="text-sm font-semibold tracking-tight text-foreground">
        {t("auth.acceptAccountantInvitation.certificateSectionTitle")}
      </h2>
      <CjppuCertificateInstructionSteps variant={variant} />
    </div>
  );
}
